"use client";

import { joinPool } from "@/actions/pool";
import { useAuth } from "@/contexts/AuthContext";
import { logTournamentEvent } from "@/lib/analytics";
import { SKILL_LEVELS, type SkillLevel, type Tournament } from "@/types";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Form,
  Radio,
  RadioGroup,
  Textarea,
} from "@heroui/react";
import { addToast } from "@heroui/toast";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useState } from "react";

interface JoinPoolFormProps {
  tournament: Tournament;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function JoinPoolForm({
  tournament,
  onSuccess,
  onCancel,
}: JoinPoolFormProps) {
  const t = useTranslations();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);

  const [ownLevel, setOwnLevel] = useState<SkillLevel | "">("");
  const [desiredLevels, setDesiredLevels] = useState<string[]>([]);
  const [note, setNote] = useState("");

  const NOTE_MAX = 200;
  const canSubmit =
    !loading && ownLevel !== "" && desiredLevels.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setLoading(true);
      const result = await joinPool({
        tournamentId: tournament.id,
        ownLevel: ownLevel as SkillLevel,
        desiredLevels: desiredLevels as SkillLevel[],
        note: note.trim() || undefined,
      });

      if (!result.success) {
        addToast({
          title: t("tournaments.pool.errors.joinFailed"),
          description: result.error,
          color: "danger",
          severity: "danger",
        });
        return;
      }

      await refreshUser();
      queryClient.invalidateQueries({
        queryKey: ["poolEntries", tournament.id],
      });

      addToast({
        title: t("tournaments.pool.success.title"),
        description: t("tournaments.pool.success.description"),
        color: "success",
        severity: "success",
      });

      logTournamentEvent("create", tournament.id);

      onSuccess?.();
      router.push(`/tournaments/${tournament.slug}/pool`);
    } catch (error) {
      console.error("Error joining pool:", error);
      addToast({
        title: t("tournaments.pool.errors.joinFailed"),
        color: "danger",
        severity: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form className="flex flex-col gap-5" onSubmit={handleSubmit}>
      <div className="space-y-1">
        <p className="text-sm text-default-400">
          {t("tournaments.pool.form.intro")}
        </p>
      </div>

      <RadioGroup
        label={t("tournaments.pool.form.ownLevelLabel")}
        description={t("tournaments.pool.form.ownLevelDescription")}
        value={ownLevel}
        onValueChange={(v) => setOwnLevel(v as SkillLevel)}
        isRequired
        orientation="horizontal"
        classNames={{
          wrapper: "gap-2 flex-wrap",
        }}
      >
        {SKILL_LEVELS.map((lvl) => (
          <Radio key={lvl} value={lvl}>
            {t(`tournaments.pool.levels.${lvl}`)}
          </Radio>
        ))}
      </RadioGroup>

      <CheckboxGroup
        label={t("tournaments.pool.form.desiredLevelsLabel")}
        description={t("tournaments.pool.form.desiredLevelsDescription")}
        value={desiredLevels}
        onValueChange={setDesiredLevels}
        isRequired
        orientation="horizontal"
        classNames={{
          wrapper: "gap-2 flex-wrap",
        }}
      >
        {SKILL_LEVELS.map((lvl) => (
          <Checkbox key={lvl} value={lvl}>
            {t(`tournaments.pool.levels.${lvl}`)}
          </Checkbox>
        ))}
      </CheckboxGroup>

      <Textarea
        label={t("tournaments.pool.form.noteLabel")}
        description={t("tournaments.pool.form.noteDescription")}
        placeholder={t("tournaments.pool.form.notePlaceholder")}
        value={note}
        onValueChange={setNote}
        maxLength={NOTE_MAX}
        minRows={2}
        maxRows={4}
        classNames={{ description: "text-xs text-default-400" }}
      />

      <div className="flex sm:flex-row gap-2 pt-3 border-t border-white/10">
        {onCancel && (
          <Button
            variant="bordered"
            onPress={onCancel}
            isDisabled={loading}
            size="lg"
          >
            {t("common.cancel")}
          </Button>
        )}

        <Button
          color="primary"
          type="submit"
          isDisabled={!canSubmit}
          isLoading={loading}
          size="lg"
        >
          {t("tournaments.pool.form.submitButton")}
        </Button>
      </div>
    </Form>
  );
}
